import { pmZoneSitesUiSlice } from '@features/store/pmZoneSitesUiReducer';
import { handleFulfilledAction, handleMultipleLoadingActions } from '@hwm/ui-lib/core/store';
import { AccountModel, PMZoneDetails, UserMainAccountModel } from '@shared/models';
import { actionInterceptor } from '../middleware/actionsInterceptorMiddleware';
import { BaseAccountStoreModel, accountsStoreName } from '@hwm/ui-lib';
import { PayloadAction, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { accountsThunks } from './accounts.thunks';
import { userProfileThunks } from './user-profile.thunks';

export interface AccountStoreState extends BaseAccountStoreModel<AccountModel, UserMainAccountModel> {
  pmZoneData: PMZoneDetails;
}

export const accountsAdapter = createEntityAdapter({
  selectId: (acc: AccountModel) => acc.id || acc['accountId'],
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});

const initialState: AccountStoreState = {
  error: null,
  hierarchy: [],
  loadingPending: 0,
  selectedAccount: null,
  subaccounts: accountsAdapter.getInitialState(),
  userRole: '',
  userMainAccount: null,
  includeArchived: false,
  pmZoneData: null,
};

const slice = createSlice({
  name: accountsStoreName,
  initialState,
  reducers: {
    setSelectedAccount(state, action: PayloadAction<{ account: AccountModel }>) {
      state.selectedAccount = action.payload.account;
    },
    setHierarchy(state, action: PayloadAction<{ hierarchy: AccountModel[] }>) {
      state.hierarchy = action.payload.hierarchy;
    },
    accountsReceived(state, action: PayloadAction<{ accounts: AccountModel[] }>) {
      accountsAdapter.setAll(state.subaccounts, action.payload.accounts);
    },
    //
    setUserMainAccount: (state, { payload }: { payload: UserMainAccountModel }) => {
      state.userMainAccount = payload;
    },
    setUserRole: (state, { payload }: { payload: string }) => {
      state.userRole = payload;
    },
    setIncludeArchived: (state, { payload }: { payload: boolean }) => {
      state.includeArchived = payload;
    },
    //
    setPmZoneData: (state, { payload }: { payload: PMZoneDetails }) => {
      state.pmZoneData = payload;
    },
  },
  extraReducers: (builder) => {
    handleMultipleLoadingActions(
      [accountsThunks.fetchSubAccounts, accountsThunks.fetchById, accountsThunks.getPmZoneData],
      builder
    );
    handleFulfilledAction(accountsThunks.fetchSubAccounts, builder, (state, payload) => {
      accountsAdapter.setAll(state.subaccounts, payload);
    });
    handleFulfilledAction(accountsThunks.fetchById, builder, (state, payload) => {
      state.selectedAccount = payload;
      state.hierarchy = [...(payload?.ancestors ?? []), payload];
    });
    handleFulfilledAction(accountsThunks.getPmZoneData, builder, (state, payload) => {
      state.pmZoneData = payload;
    });
  },
});

export const accountsReducer = {
  slice,
};

actionInterceptor.addActionsCallback<typeof userProfileThunks.getUserProfile.fulfilled>({
  actionTypes: [userProfileThunks.getUserProfile.fulfilled.type],
  callback: ({ dispatch, action }) => {
    dispatch(slice.actions.setUserMainAccount(action.payload ?? null));

    const accountId = new URLSearchParams(window.location.search).get('accountId');

    if (accountId) {
      dispatch(accountsThunks.fetchById({ accountId }));
    } else {
      dispatch(
        slice.actions.setSelectedAccount({
          account: action.payload,
        })
      );
      dispatch(
        slice.actions.setHierarchy({
          hierarchy: [action.payload],
        })
      );
    }
  },
});

actionInterceptor.addActionsCallback<typeof accountsThunks.fetchById.fulfilled>({
  actionTypes: [accountsThunks.fetchById.fulfilled.type],
  callback: ({ dispatch, action }) => {
    const accId = action.payload?.['id'] ?? action.payload?.['accountId'];

    dispatch(
      accountsThunks.fetchSubAccounts({
        accountId: accId.toString(),
      })
    );

    if (action.payload?.isPmZone) {
      dispatch(accountsThunks.getPmZoneData({ accountId: accId }));
    } else {
      dispatch(slice.actions.setPmZoneData(null));
      dispatch(pmZoneSitesUiSlice.actions.setIds({ ids: [] }));
    }
  },
});

actionInterceptor.addActionsCallback<typeof slice.actions.setSelectedAccount>({
  actionTypes: [slice.actions.setSelectedAccount.type],
  callback: ({ action: { payload }, dispatch }) => {
    const accId = payload.account?.['id'] ?? payload.account?.['accountId'];

    if (payload.account?.isPmZone) {
      dispatch(accountsThunks.getPmZoneData({ accountId: accId }));
    } else {
      dispatch(slice.actions.setPmZoneData(null));
      dispatch(pmZoneSitesUiSlice.actions.setIds({ ids: [] }));
    }
  },
});
